import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import type {
  ApplicationStatus,
  GigApplication,
  GigBoardCounts,
  GigBoardFilter,
  GigBoardHome,
  GigKind,
  GigListing,
  GigSort,
  GigView,
} from "../../api/gigboard";
import type { Track } from "../../api/types";
import { Banner } from "../../components/Banner";
import { Button, buttonClass } from "../../components/Button";
import { inputShell } from "../../components/Field";
import { PushCard } from "../../components/PushCard";
import { ScreenHeader } from "../../components/ScreenHeader";
import { ApplyModal } from "./ApplyModal";
import { GigCard } from "./GigCard";
import { agoText, formatCount } from "./gigFormat";
import { MoreGigs } from "./MoreGigs";
import { MyApplications } from "./MyApplications";

export type GigTab = "all" | "apps";

export interface GigTile {
  listing: GigListing;
  application?: GigApplication | null | undefined;
}

interface GigBoardScreenProps {
  myTrack: Track;
  trackLabels: Record<Track, string>;
  home: GigBoardHome;
  tab: GigTab;
  filter: GigBoardFilter;
  tiles: GigTile[];
  total: number;
  sources: string[];
  updatedAt?: string | null | undefined;
  counts: GigBoardCounts;
  loadingGigs: boolean;
  gigsError?: string | null | undefined;
  onTab: (tab: GigTab) => void;
  onFilter: (patch: Partial<GigBoardFilter>) => void;
  onShowMore: () => void;
  onRetry: () => void;
  onApplied: (listing: GigListing) => void;
  onStatus: (application: GigApplication, status: ApplicationStatus) => void;
}

function BackToSteps() {
  return (
    <div className="mt-4 flex flex-wrap gap-2.5">
      <Link to="/hub" className={buttonClass("ghost")}>
        ← Back to my steps
      </Link>
    </div>
  );
}

/** Nothing collected yet: the first refresh hasn't run (or found nothing). */
export function GigBoardEmpty({
  myTrack,
  trackLabels,
}: {
  myTrack: Track;
  trackLabels: Record<Track, string>;
}) {
  return (
    <div>
      <ScreenHeader
        kicker="Step 6 · Gig Board"
        title="The first gigs are on their way."
        sub="The Gig Board collects listings from job sources several times a day. Check back in a little while."
      />
      <MoreGigs myTrack={myTrack} trackLabels={trackLabels} />
      <BackToSteps />
    </div>
  );
}

function ScoreCard({ label, value, detail }: { label: string; value: string; detail: ReactNode }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
      <small className="text-[11px] font-bold uppercase tracking-[0.08em] text-text-muted">
        {label}
      </small>
      <div className="font-[family-name:var(--font-display)] text-[26px] font-extrabold text-accent tabular">
        {value}
      </div>
      <div className="text-[12.5px] text-text-muted">{detail}</div>
    </div>
  );
}

function Select<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: [T, string][];
  onChange: (value: T) => void;
}) {
  return (
    <select
      aria-label={label}
      className="min-h-11 max-w-full rounded-[var(--radius-control)] border-[1.5px] border-border bg-surface px-2.5 text-[13.5px]"
      value={value}
      onChange={(event) => onChange(event.target.value as T)}
    >
      {options.map(([v, t]) => (
        <option key={v} value={v}>
          {t}
        </option>
      ))}
    </select>
  );
}

const TRACK_KEYS: Track[] = ["uiux", "graphic", "video", "digital"];

/** Step 6 (v2): the Gig Board. Ongoing, never "complete": real listings, My applications and Report a Win. */
export function GigBoardScreen(props: GigBoardScreenProps) {
  const { home, filter, counts, myTrack, trackLabels } = props;
  const [applying, setApplying] = useState<GigListing | null>(null);
  const myLabel = trackLabels[myTrack];

  const tabButton = (tab: GigTab, label: string) => (
    <Button
      size="sm"
      variant={props.tab === tab ? "primary" : "ghost"}
      aria-pressed={props.tab === tab}
      onClick={() => props.onTab(tab)}
    >
      {label}
    </Button>
  );

  return (
    <div>
      <ScreenHeader
        kicker="Step 6 · Gig Board"
        title={`Real gigs, picked for ${myLabel}.`}
        sub="Fresh listings every day from trusted job sources, open to people in Nigeria. Tap Apply to go to the original listing, use your Step 3 bid structure, and tell us how it went."
      />
      <PushCard onBoard />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <ScoreCard
          label="New in the last 2 days"
          value={formatCount(counts.fresh)}
          detail={`${formatCount(counts.mine)} in your track, ${formatCount(counts.transferable)} transferable, ${formatCount(counts.all)} in all`}
        />
        <ScoreCard
          label="Applied this week"
          value={formatCount(home.appliedThisWeek)}
          detail={`${formatCount(home.applications.length)} in total`}
        />
        <ScoreCard
          label="Paid wins"
          value={formatCount(home.paidWins)}
          detail={
            <Link to="/gig/6/win" className="text-accent underline">
              Report a win
            </Link>
          }
        />
      </div>
      <div className="mb-1.5 mt-[18px] flex flex-wrap gap-2">
        {tabButton("all", "All gigs")}
        {tabButton("apps", `My applications (${home.applications.length})`)}
        <Link to="/gig/6/win" className={buttonClass("ghost", "sm")}>
          + Report a win
        </Link>
      </div>

      {props.tab === "apps" ? (
        <MyApplications applications={home.applications} onStatus={props.onStatus} />
      ) : (
        <>
          <div className="mb-3 mt-2 flex flex-wrap items-center gap-2">
            <input
              type="search"
              aria-label="Search gigs"
              placeholder="Search gigs"
              className={`${inputShell} min-w-[200px] flex-1`}
              value={filter.search ?? ""}
              onChange={(event) => props.onFilter({ search: event.target.value })}
            />
            <Select<GigView>
              label="Show gigs from"
              value={filter.view}
              onChange={(view) => props.onFilter({ view })}
              options={[
                ["rec", "My skill + transferable skills"],
                ["mine", `Only my skill: ${myLabel}`],
                [
                  "transferable",
                  "Transferable skills: virtual assistant, customer experience, sales, support, data and AI training",
                ],
                ["all", "All skills"],
                ...TRACK_KEYS.filter((k) => k !== myTrack).map((k): [GigView, string] => [
                  k,
                  trackLabels[k],
                ]),
              ]}
            />
            <Select<GigKind>
              label="Type"
              value={filter.kind}
              onChange={(kind) => props.onFilter({ kind })}
              options={[
                ["any", "Any type"],
                ["gig", "Gigs and contracts"],
                ["job", "Full-time jobs"],
              ]}
            />
            <Select<string>
              label="Source"
              value={filter.source ?? ""}
              onChange={(source) => props.onFilter({ source: source || null })}
              options={[["", "All sources"], ...props.sources.map((s): [string, string] => [s, s])]}
            />
            <Select<GigSort>
              label="Sort"
              value={filter.sort}
              onChange={(sort) => props.onFilter({ sort })}
              options={[
                ["mixed", "Mixed sources"],
                ["newest", "Newest first"],
                ["fewest", "Fewest bids first"],
              ]}
            />
            <label className="flex items-center gap-1.5 text-[13px]">
              <input
                type="checkbox"
                checked={filter.englishOnly}
                onChange={(event) => props.onFilter({ englishOnly: event.target.checked })}
              />{" "}
              English only
            </label>
          </div>
          {props.gigsError ? (
            <Banner tone="bad">
              We couldn't load gigs just now. Check your connection, then{" "}
              <button type="button" className="underline" onClick={props.onRetry}>
                try again
              </button>
              .
            </Banner>
          ) : null}
          <p className="text-[13px] text-text-muted">
            {props.total.toLocaleString("en-US")} listing{props.total === 1 ? "" : "s"}
            {props.updatedAt ? ` · updated ${agoText(props.updatedAt).toLowerCase()}` : ""}
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {props.tiles.map((tile) => (
              <GigCard
                key={tile.listing.listingKey}
                listing={tile.listing}
                application={tile.application}
                myTrack={myTrack}
                trackLabels={trackLabels}
                onApply={() => setApplying(tile.listing)}
              />
            ))}
          </div>
          {!props.tiles.length && !props.loadingGigs ? (
            <p className="text-[13px] text-text-muted">
              Nothing matches. Try a different search or filter.
            </p>
          ) : null}
          {props.tiles.length < props.total ? (
            <div className="mt-3">
              <Button variant="ghost" disabled={props.loadingGigs} onClick={props.onShowMore}>
                {props.loadingGigs ? "Loading..." : "Show more"}
              </Button>
            </div>
          ) : null}
        </>
      )}

      <MoreGigs myTrack={myTrack} trackLabels={trackLabels} />
      <BackToSteps />

      {applying ? (
        <ApplyModal
          listing={applying}
          onContinue={() => props.onApplied(applying)}
          onClose={() => setApplying(null)}
        />
      ) : null}
    </div>
  );
}
